import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";

const REASONS = ["vacation", "sick", "training", "day_off", "private", "other"];

/** Fixed reason → badge color mapping. */
const REASON_COLORS = {
  vacation:  "bg-emerald-500/25 border-emerald-500/50 text-emerald-100",
  sick:      "bg-red-500/25     border-red-500/50     text-red-100",
  training:  "bg-blue-500/25    border-blue-500/50    text-blue-100",
  day_off:   "bg-zinc-500/25    border-zinc-500/50    text-zinc-100",
  private:   "bg-purple-500/25  border-purple-500/50  text-purple-100",
  other:     "bg-amber-500/25   border-amber-500/50   text-amber-100",
};

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

/** Yield every YYYY-MM-DD between start & end inclusive. */
function eachDay(start, end) {
  const out = [];
  let cur = new Date(start);
  const stop = new Date(end);
  while (cur <= stop) { out.push(iso(cur)); cur = addDays(cur, 1); }
  return out;
}

export default function Absences() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const canEdit = user?.role === "owner" || user?.role === "admin";

  const [employees, setEmployees] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [view, setView] = useState("month");                     // month | year
  const [cursor, setCursor] = useState(new Date());              // any date within view
  const [dialog, setDialog] = useState(null);                    // {mode:'create'|'edit', ...}
  const [form, setForm] = useState({ employee_id: "", start_date: "", end_date: "", reason: "vacation", note: "" });

  const load = async () => {
    const [e, a] = await Promise.all([api.get("/employees"), api.get("/absences")]);
    setEmployees(e.data);
    setAbsences(a.data);
  };
  useEffect(() => { load(); }, []);

  // ---------- lookups ----------
  const byEmployee = useMemo(() => {
    const m = {};
    for (const a of absences) (m[a.employee_id] = m[a.employee_id] || []).push(a);
    Object.values(m).forEach((l) => l.sort((x, y) => x.start_date.localeCompare(y.start_date)));
    return m;
  }, [absences]);

  const absenceOnDay = (empId, day) =>
    (byEmployee[empId] || []).find((a) => a.start_date <= day && day <= a.end_date);

  // ---------- CRUD ----------
  const openCreate = (empId, day) => {
    if (!canEdit) return;
    setForm({ employee_id: empId, start_date: day, end_date: day, reason: "vacation", note: "" });
    setDialog({ mode: "create" });
  };
  const openEdit = (absence) => {
    if (!canEdit) return;
    setForm({ ...absence });
    setDialog({ mode: "edit", absence });
  };
  const save = async () => {
    if (!form.employee_id || !form.start_date || !form.end_date) return;
    if (form.end_date < form.start_date) { toast.error(t("absence.error_range")); return; }
    try {
      if (dialog.mode === "edit") {
        await api.patch(`/absences/${dialog.absence.id}`, {
          start_date: form.start_date, end_date: form.end_date,
          reason: form.reason, note: form.note,
        });
      } else {
        await api.post("/absences", form);
      }
      toast.success("OK");
      setDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const removeFull = async () => {
    if (!dialog?.absence) return;
    const ok = await confirm({
      title: t("absence.confirm_delete", { defaultValue: "Abwesenheit löschen?" }),
      description: "", danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/absences/${dialog.absence.id}`);
      toast.success("OK");
      setDialog(null);
      load();
    } catch { toast.error("Error"); }
  };
  const removeDay = async (absence, day) => {
    if (!canEdit) return;
    const ok = await confirm({
      title: t("absence.confirm_delete_day", { defaultValue: "Diesen Tag entfernen?" }),
      description: day, danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/absences/${absence.id}`, { params: { date: day } });
      toast.success("OK");
      load();
    } catch { toast.error("Error"); }
  };

  // ---------- navigation ----------
  const shiftCursor = (dir) => {
    const c = new Date(cursor);
    if (view === "month") c.setMonth(c.getMonth() + dir);
    else                  c.setFullYear(c.getFullYear() + dir);
    setCursor(c);
  };
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const yearLabel = cursor.getFullYear();

  return (
    <div className="space-y-4" data-testid="absences-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("nav.absences")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("nav.absences")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg overflow-hidden border border-[#2E3241] bg-[#171B24] text-xs"
               data-testid="absences-view-toggle">
            {["month", "year"].map((v) => (
              <button key={v} type="button" onClick={() => setView(v)}
                className={`px-3 py-1.5 uppercase tracking-wider ${
                  view === v ? "bg-blue-500/20 text-blue-200" : "text-zinc-400 hover:text-zinc-200"
                }`}
                data-testid={`absences-view-${v}`}>
                {t(`absence.view_${v}`)}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center rounded-lg border border-[#2E3241] bg-[#171B24]">
            <button className="px-2 py-1 text-zinc-400 hover:text-zinc-100" onClick={() => shiftCursor(-1)}
              data-testid="absences-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button className="px-3 text-xs text-zinc-200 min-w-[140px]" onClick={() => setCursor(new Date())}
              data-testid="absences-today">{view === "month" ? monthLabel : yearLabel}</button>
            <button className="px-2 py-1 text-zinc-400 hover:text-zinc-100" onClick={() => shiftCursor(1)}
              data-testid="absences-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <Legend t={t} />

      {view === "month"
        ? <MonthView cursor={cursor} employees={employees} absenceOnDay={absenceOnDay}
                     canEdit={canEdit}
                     onCellClick={(empId, day) => {
                       const a = absenceOnDay(empId, day);
                       if (a) openEdit(a); else openCreate(empId, day);
                     }}
                     onRemoveDay={(a, d) => removeDay(a, d)} />
        : <YearView cursor={cursor} employees={employees} byEmployee={byEmployee}
                    onMonthClick={(empId, day) => {
                      const a = absenceOnDay(empId, day);
                      if (a) openEdit(a); else openCreate(empId, day);
                    }} />}

      {/* Add/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#21252E] border-[#2E3241] text-white">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? t("absence.edit") : t("absence.new")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("employees.title")}</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="absence-emp"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("absence.start")}</Label>
                <Input type="date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="absence-start" />
              </div>
              <div>
                <Label className="text-xs">{t("absence.end")}</Label>
                <Input type="date" value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="absence-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("absence.reason")}</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="absence-reason"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {REASONS.map((r) => <SelectItem key={r} value={r}>{t(`absence.reasons.${r}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("absence.note")}</Label>
              <Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]" data-testid="absence-note" />
            </div>
          </div>
          <DialogFooter className="flex-row justify-between">
            <div>
              {dialog?.mode === "edit" && (
                <Button variant="ghost" onClick={removeFull} className="text-red-400 hover:text-red-300"
                  data-testid="absence-delete-full">
                  <Trash2 className="h-4 w-4 mr-1" /> {t("common.delete")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
              <Button onClick={save}
                disabled={!form.employee_id || !form.start_date || !form.end_date || form.end_date < form.start_date}
                className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
                data-testid="absence-save">
                {dialog?.mode === "edit" ? t("common.save") : (<><Plus className="h-4 w-4 mr-1" />{t("common.create")}</>)}
              </Button>
            </div>
          </DialogFooter>
          {/* single-day trim helper: only when editing a multi-day record */}
          {dialog?.mode === "edit" && dialog.absence.start_date !== dialog.absence.end_date && (
            <div className="text-[11px] text-zinc-500 text-center pt-1"
                 data-testid="absence-day-trim-hint">
              {t("absence.day_trim_hint", {
                defaultValue: "Tipp: Klick auf einen einzelnen Tag im Kalender entfernt nur diesen Tag.",
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Legend({ t }) {
  return (
    <div className="flex flex-wrap gap-2 text-[11px]">
      {REASONS.map((r) => (
        <span key={r} className={`px-2 py-0.5 rounded border ${REASON_COLORS[r]}`}
          data-testid={`legend-${r}`}>
          {t(`absence.reasons.${r}`)}
        </span>
      ))}
    </div>
  );
}

function MonthView({ cursor, employees, absenceOnDay, onCellClick, onRemoveDay, canEdit }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const total = daysInMonth(year, month);
  const days = Array.from({ length: total }, (_, i) => iso(new Date(year, month, i + 1)));

  return (
    <div className="surface rounded-lg overflow-x-auto" data-testid="absences-month">
      <div className="min-w-[900px]">
        <div className="grid" style={{ gridTemplateColumns: `220px repeat(${total}, minmax(28px, 1fr))` }}>
          <div className="px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-[#2E3241]">
            {/* header spacer */}
          </div>
          {days.map((d, i) => {
            const dow = new Date(d).getDay();
            const isWeekend = dow === 0 || dow === 6;
            return (
              <div key={d} className={`text-center text-[10px] py-2 border-b border-[#2E3241] font-mono-stat ${isWeekend ? "text-zinc-600" : "text-zinc-400"}`}>
                {i + 1}
              </div>
            );
          })}
          {employees.map((e) => (
            <React.Fragment key={e.id}>
              <div className="px-3 py-2 text-sm text-zinc-200 border-b border-[#2E3241] sticky left-0 bg-[#21252E]">
                {e.first_name} {e.last_name}
              </div>
              {days.map((d) => {
                const a = absenceOnDay(e.id, d);
                const cls = a ? REASON_COLORS[a.reason] || REASON_COLORS.other : "";
                return (
                  <div key={d} className={`group relative h-8 border-b border-r border-[#2E3241]/60 ${a ? cls : ""}`}>
                    <button
                      onClick={() => onCellClick(e.id, d)}
                      className="absolute inset-0 hover:bg-white/5 transition-colors"
                      title={a ? `${a.reason} · ${a.start_date} → ${a.end_date}` : d}
                      data-testid={`abs-cell-${e.id}-${d}`}
                    />
                    {a && canEdit && (
                      <button
                        onClick={(ev) => { ev.stopPropagation(); onRemoveDay(a, d); }}
                        className="absolute top-0.5 right-0.5 h-4 w-4 rounded-sm bg-black/40 text-white/80
                                   hover:bg-red-500/70 hover:text-white text-[10px] leading-4
                                   opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        title="Diesen Tag entfernen"
                        data-testid={`abs-day-remove-${e.id}-${d}`}
                      >×</button>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function YearView({ cursor, employees, byEmployee, onMonthClick }) {
  const year = cursor.getFullYear();
  const months = Array.from({ length: 12 }, (_, m) => m);
  const monthName = (m) => new Date(year, m, 1).toLocaleDateString(undefined, { month: "short" });

  const monthAbsences = (empId, month) => {
    const first = iso(new Date(year, month, 1));
    const last = iso(new Date(year, month + 1, 0));
    return (byEmployee[empId] || []).filter((a) => a.end_date >= first && a.start_date <= last);
  };

  return (
    <div className="surface rounded-lg p-3 overflow-x-auto" data-testid="absences-year">
      <div className="min-w-[900px] grid" style={{ gridTemplateColumns: "220px repeat(12, 1fr)" }}>
        <div />
        {months.map((m) => (
          <div key={m} className="text-center text-[11px] uppercase tracking-widest text-zinc-400 py-2">
            {monthName(m)}
          </div>
        ))}
        {employees.map((e) => (
          <React.Fragment key={e.id}>
            <div className="px-3 py-2 text-sm text-zinc-200 border-t border-[#2E3241]">
              {e.first_name} {e.last_name}
            </div>
            {months.map((m) => {
              const list = monthAbsences(e.id, m);
              const first = iso(new Date(year, m, 1));
              return (
                <button key={m}
                  onClick={() => onMonthClick(e.id, first)}
                  className="border-t border-l border-[#2E3241]/50 min-h-[54px] p-1 flex flex-col gap-0.5 text-left hover:bg-[#2A2F3A]"
                  data-testid={`abs-year-cell-${e.id}-${year}-${m}`}
                >
                  {list.slice(0, 3).map((a) => (
                    <span key={a.id}
                      className={`text-[10px] px-1 py-0.5 rounded border truncate ${REASON_COLORS[a.reason] || REASON_COLORS.other}`}>
                      {a.reason.slice(0, 3)} {a.start_date.slice(8, 10)}
                      {a.end_date !== a.start_date ? `-${a.end_date.slice(8, 10)}` : ""}
                    </span>
                  ))}
                  {list.length > 3 && <span className="text-[9px] text-zinc-500">+{list.length - 3}</span>}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
